import _ from 'lodash'
import { createRef } from '@wix/materializer'
import {
	fixSharedPartsIds,
	getInflatedIdAncestorChain,
	hasSharedPartsPrefix,
	REF_DELIMITER,
	removeSharedPartsPrefix,
} from '@wix/thunderbolt-commons'
import type {
	Component,
	ComponentLayout,
	DataMapName,
	InternalRefDataItem,
	LayoutDataItems,
	QueryProperty,
	SingleLayoutData,
	Style,
	ViewMode,
	WidgetRefDataItem,
} from '@wix/thunderbolt-becky-types'
import type { GhostPropertiesItem } from '@wix/thunderbolt-becky-root'
import type { ScopedCreateRef } from './compNodeUtils'
import type { RawModel } from '../types'
import {
	DATA_MAP_TO_QUERY,
	ENVIRONMENT_NAMESPACE,
	EXPERIMENTS_ASPECT,
	HAS_REMOTE_OVERRIDE,
	HAS_SITE_OVERRIDE,
	LEAFS_OVERRIDES_DELIMITER,
	QUERY_MAPPING,
	RESOLVED_REFS_NAMESPACE,
} from './constants'

export const isRefComponent = (component?: Component) => component?.componentType.endsWith('RefComponent')

export const isRefCompConsumed = (component: Component) =>
	isRefComponent(component) && component.components && component.components.length > 0

export const isSharedPart = (dataItem: InternalRefDataItem) => !dataItem.pageId || dataItem.pageId === 'masterPage'

export const getRefCreatorInNamespace = (namespace: string): ScopedCreateRef => (key2, key3, defaultValue) =>
	createRef([namespace, key2, key3], defaultValue)
export const createRefInResolvedRefsNamespaces = getRefCreatorInNamespace(RESOLVED_REFS_NAMESPACE)

export const createExperimentRef = (experimentName: string) =>
	createRef<boolean>([ENVIRONMENT_NAMESPACE, EXPERIMENTS_ASPECT, experimentName])

export const isInflatedId = (id: string) => removeSharedPartsPrefix(id).includes(REF_DELIMITER)

// "ref1_r_ref2_r_compId-.." => "ref1_r_ref2"
export const getContextFromInflatedId = (inflatedId: string) => {
	const contextEndIndex = inflatedId.lastIndexOf(REF_DELIMITER)
	return inflatedId.substring(0, contextEndIndex)
}

// "ref1_r_ref2_r_compId" => { context: "ref1_r_ref2", templateCompId: "compId" }
export const getContextAndTemplateCompIdFromInflatedCompId = (inflatedCompId: string) => {
	const contextEndIndex = inflatedCompId.lastIndexOf(REF_DELIMITER)
	return {
		templateCompId: inflatedCompId.substring(contextEndIndex + REF_DELIMITER.length),
		context: inflatedCompId.substring(0, contextEndIndex),
	}
}

// inflated variables dataItems can contain the queryName ('variablesQuery') or 'variables'
const dataMapToDataPrefixInInflatedDataId: Record<DataMapName, string> = { ...DATA_MAP_TO_QUERY, variables: 'variable' }
// "ref1_r_ref2_r_compId-..." => { context: "ref1_r_ref2", templateCompId: "compId", inflatedCompId: "ref1_r_ref2_r_compId" }
export const getContextAndTemplateCompIdFromInflatedDataId = (inflatedDataId: string, dataMapName: DataMapName) => {
	const dataPrefix = dataMapToDataPrefixInInflatedDataId[dataMapName]
	const contextEndIndex = inflatedDataId.lastIndexOf(REF_DELIMITER)
	const templateCompIdIndex = contextEndIndex + REF_DELIMITER.length
	const dataPrefixIndex = inflatedDataId.indexOf(`-${dataPrefix}`, templateCompIdIndex)
	const context = inflatedDataId.substring(0, contextEndIndex)
	const templateCompId = inflatedDataId.substring(templateCompIdIndex, dataPrefixIndex)
	return {
		templateCompId,
		context,
		inflatedCompId: `${context}${REF_DELIMITER}${templateCompId}`,
	}
}

// "ref1_r_ref2 => ["ref1", "ref1_r_ref2"]
export const getRefCompIds = (refCompId: string): Array<string> => {
	const hasSharedPartPrefix = hasSharedPartsPrefix(refCompId)
	const compId = hasSharedPartPrefix ? removeSharedPartsPrefix(refCompId) : refCompId
	const res: Array<string> = []
	compId.split(REF_DELIMITER).forEach((refId, index) => {
		if (index > 0) {
			res.push(`${res[index - 1]}${REF_DELIMITER}${refId}`)
		} else {
			res.push(hasSharedPartPrefix ? fixSharedPartsIds(refId) : refId)
		}
	})
	return res
}

export const getSplitCompIdsFromInflatedId = (inflatedCompId: string) => {
	return inflatedCompId.split(REF_DELIMITER)
}

export const getLeafsOverridesMapAspectName = (context: string, queryName: QueryProperty) =>
	`${context}${LEAFS_OVERRIDES_DELIMITER}${queryName}LeafsOverridesMap`

export const getQueryNameFromLeafsOverridesMapAspectName = (aspectName: string) =>
	aspectName.split(LEAFS_OVERRIDES_DELIMITER)[1].replace('LeafsOverridesMap', '') as QueryProperty

export const getViewModeSuffix = (viewMode: ViewMode) => `_${viewMode}`

// TODO: un-optionalize viewMode once byRefsis is merged
export const getRefTemplateKey = (refDataItem: WidgetRefDataItem | InternalRefDataItem, viewMode?: ViewMode) => {
	const viewModeSuffix = viewMode ? getViewModeSuffix(viewMode) : ''

	return refDataItem.type === 'WidgetRef'
		? `${refDataItem.appDefinitionId}_${refDataItem.widgetId}${viewModeSuffix}`
		: `${refDataItem.pageId || 'masterPage'}_${refDataItem.rootCompId}${viewModeSuffix}`
}

export const isGhostPropertyItem = (propItem?: GhostPropertiesItem) => propItem?.ghost === 'COLLAPSED'

export const isSystemStyle = (style: Style) => style.type === 'TopLevelStyle' && style.styleType === 'system'

export const resolveSkin = (skin?: string) => {
	if (skin) {
		return skin.startsWith('svgshape.') ? skin : skin.split('.').pop()
	}
	return skin
}

export const isConnectionItem = (id: string) => id.startsWith('connection')

export const markRemoteOverride = (
	rawModel: RawModel,
	queryName: QueryProperty,
	partiallyInflatedId: string,
	value: boolean
) => {
	const remoteOverridesPath = [HAS_REMOTE_OVERRIDE, QUERY_MAPPING[queryName]]
	if (!_.has(rawModel, remoteOverridesPath)) {
		_.set(rawModel, remoteOverridesPath, new Set<string>())
	}
	const remoteOverrides = _.get(rawModel, remoteOverridesPath)
	value ? remoteOverrides.add(partiallyInflatedId) : remoteOverrides.delete(partiallyInflatedId)
}

export const hasRemoteOverride = (rawModel: RawModel, queryName: QueryProperty, inflatedCompId: string) => {
	const remoteOverrides = _.get(rawModel, [HAS_REMOTE_OVERRIDE, QUERY_MAPPING[queryName]]) as Set<string>
	if (!remoteOverrides) {
		return false
	}

	for (const partiallyInflatedCompId of remoteOverrides) {
		if (inflatedCompId.endsWith(partiallyInflatedCompId)) {
			return true
		}
	}

	return false
}

export const hasSiteOverride = (rawModel: RawModel, dataMapName: DataMapName, inflatedCompId: string) => {
	const siteOverrides = _.get(rawModel, [HAS_SITE_OVERRIDE, dataMapName])
	if (!siteOverrides) {
		return false
	}

	if (siteOverrides[inflatedCompId]) {
		return true
	}

	// in responsive, when we have an override on a master page, we write it to the HAS_SITE_OVERRIDE namespace under a partially inflated id
	// https://github.com/wix-private/thunderbolt/blob/ba243e9948bf7c6d9990f457918ef42f421baee4/packages/thunderbolt-byref/src/byRefsis/modelUpdate.ts#L195
	// TODO consider writing master page model updates in responsive to a different namespace (not SITE_NAMESPACE), so that overrides are marked under HAS_REMOTE_OVERRIDE namespace.
	// This required also for multiple layers of blocks widget in widget on the same app.
	return getInflatedIdAncestorChain(inflatedCompId).some((ancestor) => siteOverrides[ancestor])
}

export const isHiddenInAllVariants = (layoutDataItems: Array<LayoutDataItems>) =>
	_.every(layoutDataItems, (layoutDataItem) => {
		switch (layoutDataItem.type) {
			case 'SingleLayoutData':
				return (layoutDataItem as SingleLayoutData).componentLayout?.hidden
			case 'ComponentLayout':
				return (layoutDataItem as ComponentLayout).hidden
			default:
				return true
		}
	})

export const getDataPathForInnerElement = ({
	ancestorChain,
	compIdToNicknameMap,
	firstInnerWidgetId,
	extraPath,
}: {
	ancestorChain: Array<string>
	compIdToNicknameMap: Record<string, string>
	firstInnerWidgetId: string
	extraPath?: Array<string>
}) =>
	ancestorChain
		.reduce(
			(accumulatedPath, compId) => {
				const nickname = compIdToNicknameMap[compId]
				return [...accumulatedPath, 'innerElementsQuery', nickname]
			},
			[compIdToNicknameMap[firstInnerWidgetId]]
		)
		.concat(extraPath || [])
